import React, { useEffect, useState } from 'react';
import * as mentorApi from '../api/mentorApi';

/**
 * Filtration screen: lists completed sessions (per-cohort for group sessions)
 * with attendance completion status, and deep-links into the existing
 * SessionDetail attendance block to mark/edit. No new marking UI here —
 * this is navigation only; SessionAttendance.js remains the single place
 * attendance is actually recorded.
 */
export default function AttendanceOverview({ sessions, cohorts, onSessionClick }) {
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all | complete | incomplete

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    mentorApi.getAttendanceOverview()
      .then((rows) => { if (!cancelled) setOverview(rows); })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load attendance overview:', err);
        setError('Could not load attendance overview.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const rows = overview
    .map((row) => {
      const session = sessions.find((s) => String(s.id) === String(row.session_id));
      const cohort = cohorts.find((c) => String(c.id) === String(row.cohort_id));
      return {
        ...row,
        session,
        cohortName: row.cohort_name || cohort?.name || `Cohort #${row.cohort_id}`,
      };
    })
    .filter((row) => row.session)
    .filter((row) => {
      if (filter === 'complete') return row.is_complete;
      if (filter === 'incomplete') return !row.is_complete;
      return true;
    })
    .sort((a, b) => (b.scheduled_at || '').localeCompare(a.scheduled_at || ''));

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr.length === 10 ? dateStr + 'T00:00:00' : dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="animate-fade-in">
      <div className="dashboard-title-row" style={{ marginBottom: '24px' }}>
        <div>
          <h1 className="dashboard-title">Attendance</h1>
          <p className="dashboard-subtitle">Completed sessions, filtered by whether attendance has been marked.</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {[
          { key: 'all', label: 'All' },
          { key: 'incomplete', label: 'Not Marked' },
          { key: 'complete', label: 'Marked' },
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            className={filter === f.key ? 'btn-secondary-action' : 'btn-form-toggle-outlined'}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ fontStyle: 'italic', color: 'var(--text-muted)', fontSize: '13px' }}>Loading…</div>
      ) : error ? (
        <div style={{ color: '#b91c1c', fontSize: '13px' }}>{error}</div>
      ) : rows.length === 0 ? (
        <div style={{ fontStyle: 'italic', color: 'var(--text-muted)', fontSize: '13px' }}>No completed sessions match this filter.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {rows.map((row) => (
            <div
              key={`${row.session_id}-${row.cohort_id}`}
              className="checklist-row-item"
              style={{
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '12px 16px',
                cursor: 'pointer',
                background: '#fff',
              }}
              onClick={() => onSessionClick(row.session)}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--neutral-800)' }}>
                  {row.session.sessionType} — {row.cohortName}
                  {row.is_group_session && <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>(group session)</span>}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{formatDate(row.scheduled_at)}</div>
              </div>
              <span
                className="checklist-row-badge"
                style={row.is_complete
                  ? { background: '#dcfce7', color: '#15803d' }
                  : { background: '#fef3c7', color: '#b45309' }}
              >
                {row.is_complete ? 'Marked' : 'Not marked'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
